import { ReactElement } from "react";
import { Box, Center, Flex, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ResultDashboardData } from "../../types";
import { ResultDiagram } from "../atoms/ResultDiagram";
import { AnalysisResultCardLayout } from "../atoms/AnalysisResultCardLayout";
import MeterDisplay from "../atoms/MerterDisplay";
import { SimpleBar } from "../atoms/HorizontalBarChart";
import { BannerImage } from "../atoms/BannerImage";
import { Layout } from "../atoms/Layout";

export function ResultDashboard(
  props: Readonly<{
    title: string;
    result: ResultDashboardData;
  }>
): ReactElement {
  const { t } = useTranslation();
  const mainValue = props.result.mainParam.value;
  const recorded_at = props.result.subParams[3]?.value ?? "";
  const recorded_atFormatted =
    typeof recorded_at === "string"
      ? recorded_at.replace("T", " ").replace(/-/g, "/")
      : recorded_at;
  return (
    <>
      <Layout.Title showBackToHomeButton={false} fontSize="24px">
        <BannerImage />
      </Layout.Title>
      <Box marginBottom="10px" marginTop="-8px">
        <Center
          h="40px"
          bg="primary.bg_lv1"
          color="text.main_text_lv1"
          fontWeight="bold"
          fontSize="24px"
        >
          {props.title}
        </Center>
        <div
          style={{
            marginTop: "-4px",
            marginBottom: "16px",
            backgroundColor: "primary.bg_lv1",
            color: "text.main_text_lv1",
            fontSize: "12px",
            textAlign: "center",
          }}
        >
          解析日時： {recorded_atFormatted}
        </div>
      </Box>

      <AnalysisResultCardLayout title={t("Result.Mi1_vitality")}>
        <Flex gap={2}>
          {props.result.supplementaryInfo.map((info, infoIndex) => {
            return (
              <Box key={infoIndex} fontSize="12px">
                {t(info.title) +
                  ":" +
                  (info.value !== undefined ? info.value : "Error")}
                {infoIndex !== props.result.supplementaryInfo.length - 1 &&
                  ", "}
              </Box>
            );
          })}
        </Flex>
        <div
          style={{
            height: "min(300px, 65vw)",
            width: "100%",
            maxWidth: "400px",
            margin: "0 auto",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <MeterDisplay value={Number(mainValue)} />
          <div
            style={{
              position: "absolute",
              textAlign: "center",
              top: "calc(80% - 20px)",
              fontSize: "clamp(20px, 4vw, 24px)",
              width: "100%",
              height: "100%",
            }}
          >
            {props.result.mainParam.value !== undefined
              ? props.result.mainParam.value
              : "Error"}
          </div>
        </div>
        {/* <DashboardBox result={props.result} /> */}
        {/* メーター動作確認用 */}
        {/* <MeterDisplayDemo /> */}
      </AnalysisResultCardLayout>
      <AnalysisResultCardLayout title={t("Result.Mi1_mental_activity")}>
        <VStack
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="20px"
          width="100%"
          bg="white"
          paddingTop="16px"
          paddingBottom="8px"
        >
          <SimpleBar
            value={Number(props.result.subParams[2].value)}
            maxValue={100}
            height="36px"
          />
          <div
            style={{
              paddingTop: "4px",
              display: "grid",
              gridTemplateColumns: "25% 50% 25%",
              width: "100%",
            }}
          >
            <span style={{ background: "#58C0CE", height: 10 }} />
            <span style={{ background: "#DED21E", height: 10 }} />
            <span style={{ background: "#F78E2B", height: 10 }} />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "25% 50% 25%",
              width: "100%",
              fontSize: "12px",
              height: "8px",
              marginTop: "4px",
            }}
          >
            <span style={{ textAlign: "center" }}>低い</span>
            <span style={{ textAlign: "center" }}>標準</span>
            <span style={{ textAlign: "center" }}>高い</span>
          </div>
        </VStack>
      </AnalysisResultCardLayout>
      <AnalysisResultCardLayout title="感情バランス">
        {props.result.diagramParams.length !== 0 && (
          <ResultDiagram
            diagramParams={props.result.diagramParams}
            diagramIndex={props.result.diagramIndex}
          />
        )}
        {/* 凡例 */}
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            fontSize: "12px",
            flexWrap: "nowrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: "#f5ce00",
                borderRadius: "2px",
              }}
            />
            <span style={{ whiteSpace: "nowrap" }}>喜び</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: "#d85336",
                borderRadius: "2px",
              }}
            />
            <span style={{ whiteSpace: "nowrap" }}>怒り・主張</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: "#2581c5",
                borderRadius: "2px",
              }}
            />
            <span style={{ whiteSpace: "nowrap" }}>哀しみ</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: "#6eb759",
                borderRadius: "2px",
              }}
            />
            <span style={{ whiteSpace: "nowrap" }}>平常</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: "#ED6D00",
                borderRadius: "2px",
              }}
            />
            <span style={{ whiteSpace: "nowrap" }}>興奮</span>
          </div>
        </div>
      </AnalysisResultCardLayout>
    </>
  );
}
